import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import Header from './components/Header';
import './App.css';

// Lazy load components
const GarageList = lazy(() => import('./components/GarageList'));
const GarageDetail = lazy(() => import('./components/GarageDetail'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Profile = lazy(() => import('./components/Profile'));
const AddGarage = lazy(() => import('./components/AddGarage'));
const EditGarage = lazy(() => import('./components/EditGarage'));
const MyGarages = lazy(() => import('./components/MyGarages'));
const MyBookings = lazy(() => import('./components/MyBookings'));
const Notifications = lazy(() => import('./components/Notifications'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const DisputeResolution = lazy(() => import('./components/DisputeResolution'));
const BugReportAdmin = lazy(() => import('./components/BugReportAdmin'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }}>
    <div style={{
      textAlign: 'center',
      color: 'white'
    }}>
      <div className="loading-spinner" style={{
        width: '60px',
        height: '60px',
        border: '5px solid rgba(255, 255, 255, 0.3)',
        borderTop: '5px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
      }} />
      <p style={{ fontSize: '18px', fontWeight: '600' }}>Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Router>
          <AuthProvider>
            <ThemeProvider>
              <LanguageProvider>
                <ToastProvider>
                  <div className="App">
                  <OfflineBanner />
                  <Header />
                  <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/" element={<GarageList />} />
                <Route path="/garage/:id" element={<GarageDetail />} />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/add-garage" 
                  element={
                    <ProtectedRoute>
                      <AddGarage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/my-garages" 
                  element={
                    <ProtectedRoute>
                      <MyGarages />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/my-bookings" 
                  element={
                    <ProtectedRoute>
                      <MyBookings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/notifications" 
                  element={
                    <ProtectedRoute>
                      <Notifications />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/disputes" 
                  element={
                    <ProtectedRoute>
                      <DisputeResolution />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/bug-reports" 
                  element={
                    <ProtectedRoute>
                      <BugReportAdmin />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/edit-garage/:id" 
                  element={
                    <ProtectedRoute>
                      <EditGarage />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </Suspense>
          </div>
        </ToastProvider>
      </LanguageProvider>
    </ThemeProvider>
  </AuthProvider>
</Router>
</ErrorBoundary>
</HelmetProvider>
  );
}


export default App;
